import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PlacementVideo from "@/components/PlacementTest/PlacementVideo";
import QuestionScreen from "@/components/PlacementTest/QuestionScreen";
import WelcomeScreen from "@/components/PlacementTest/WelcomeScreen";
import StreakPage from "@/pages/StreakPage";
import { callWithAuth, postPlacementResult } from "@/api/Api";
import OnboardingHeader from "@/components/Onboarding/OnboardingHeader";
import "@/TransitionsVertical.css";
import {
  createPlacementGraph,
  PlacementNode,
  Answer,
} from "@/components/PlacementTest/placementGraph";
import { useCurrentCourse } from "@/hooks/useSelectedCourse";
import InstructionsScreen from "@/components/PlacementTest/InstructionsScreen";
import { LoadingPage } from "./LoadingPage";
import {
  getCourseAndLessonForLevel,
  getFirstChapterAndLesson,
} from "@/utils/courseNavigation";
import { useUser } from "@/hooks/UseAuthProviders";
import { useLessonData } from "@/hooks/useLessonData";
import ContinueButton from "@/components/Buttons/ContinueButton";
import { FaExclamationTriangle } from "react-icons/fa";
import { useTrackPlacementTest } from "@/hooks/posthog/useTrackPlacementTest";

// Constants
const TOTAL_STEPS = 10;
const PROD_TIKTOK_COURSE_ID = 5;

// Types
type Screen =
  | "welcome"
  | "instructions"
  | "video"
  | "question"
  | "results"
  | "streak";

interface NodeHistory {
  node: PlacementNode;
  wasOnQuestion: boolean;
}

interface PlacementResult {
  nodeId: string;
  answer: Answer;
  videoId?: string;
}

// Map node IDs to lesson IDs
const videoMap: Record<string, { lessonId: number }> = {
  peppa: { lessonId: 3018 },
  vid1: { lessonId: 707 },
  vid2: { lessonId: 751 },
  vid3: { lessonId: 416 },
  vid4: { lessonId: 2095 },
  vid5: { lessonId: 1291 },
  vid6: { lessonId: 2240 },
  vid7: { lessonId: 680 },
  vid8: { lessonId: 2075 },
};

const numberToAnswer: Record<number, Answer> = {
  0: "easy",
  1: "just right",
  2: "hard",
};

const graph = createPlacementGraph();

const PlacementTestPage: React.FC = () => {
  const navigate = useNavigate();
  const { setCurrentCourseID } = useCurrentCourse();
  const { user } = useUser();
  const firstName = user?.firstName || "";
  const { trackStartFromScratch, trackBeginPlacementTest } = useTrackPlacementTest();

  // State management
  const [currentScreen, setCurrentScreen] = useState<Screen>("welcome");
  const [currentNode, setCurrentNode] = useState<PlacementNode | null>(
    graph.startNode ?? null
  );
  const [finalLevel, setFinalLevel] = useState<string | null>(null);
  const [results, setResults] = useState<PlacementResult[]>([]);
  const [selectedAnswer, setSelectedAnswer] = useState<Answer | null>(null);
  const [nodeStack, setNodeStack] = useState<NodeHistory[]>([]);

  // Get lessonId for the current node
  const lessonId = currentNode ? videoMap[currentNode.id]?.lessonId : undefined;
  const { query: lessonDataQuery } = useLessonData(lessonId ?? -1);

  // Navigation handlers
  const handleTransition = (
    func: () => void,
    direction: "forward" | "backward" = "forward"
  ) => {
    document.documentElement.style.setProperty(
      "--slide-out-animation",
      direction === "forward" ? "slide-out" : "slide-out-reverse"
    );
    document.documentElement.style.setProperty(
      "--slide-in-animation",
      direction === "forward" ? "slide-in" : "slide-in-reverse"
    );

    if (typeof document.startViewTransition === "function") {
      document.startViewTransition(func);
    } else {
      func();
    }
  };

  const navigateToNextNode = (nextNode: PlacementNode | null) => {
    if (nextNode) {
      setNodeStack([
        ...nodeStack,
        { node: currentNode!, wasOnQuestion: currentScreen === "question" },
      ]);
      setCurrentNode(nextNode);
      setCurrentScreen("video");
      setSelectedAnswer(null);
    } else {
      setCurrentNode(null);
    }
  };

  const navigateToPrevNode = () => {
    if (currentScreen === "streak") {
      handleTransition(() => setCurrentScreen("results"), "backward");
      return;
    }
    if (currentScreen === "results") {
      if (nodeStack.length > 0) {
        const prev = nodeStack[nodeStack.length - 1];
        setNodeStack(nodeStack.slice(0, -1));
        setCurrentNode(prev.node);
        setCurrentScreen(prev.wasOnQuestion ? "question" : "video");
        if (!prev.wasOnQuestion) {
          setResults(results.slice(0, -1));
        }
        setSelectedAnswer(null);
      }
      return;
    }
    if (currentScreen === "instructions") {
      setCurrentScreen("welcome");
      return;
    }
    if (currentScreen === "question") {
      setCurrentScreen("video");
      setSelectedAnswer(null);
    } else if (nodeStack.length > 0) {
      const prev = nodeStack[nodeStack.length - 1];
      setNodeStack(nodeStack.slice(0, -1));
      setCurrentNode(prev.node);
      setCurrentScreen(prev.wasOnQuestion ? "question" : "video");
      if (!prev.wasOnQuestion) {
        setResults(results.slice(0, -1));
      }
      setSelectedAnswer(null);
    } else if (currentNode?.id === "peppa") {
      setCurrentScreen("instructions");
      setCurrentNode(null);
      setResults([]);
      setNodeStack([]);
      setFinalLevel(null);
    }
  };

  const handleUserAnswer = async (answer: Answer) => {
    if (!currentNode) return;

    const nextNode = currentNode.getNext(answer);
    setResults([...results, { nodeId: currentNode.id, answer }]);

    if (nextNode?.isEndNode()) {
      const finalLevel = nextNode.getCEFRLevel();
      if (finalLevel) {
        setFinalLevel(finalLevel);
        try {
          await callWithAuth(postPlacementResult, {
            placementResult: finalLevel,
          });
        } catch (error) {
          console.error("Failed to post placement result:", error);
        }
      }
      setNodeStack([...nodeStack, { node: currentNode, wasOnQuestion: true }]);
      setCurrentNode(null);
      handleTransition(() => setCurrentScreen("results"));
      return;
    } else if (!nextNode || !lessonId) {
      setNodeStack([...nodeStack, { node: currentNode, wasOnQuestion: true }]);
      setCurrentNode(null);
      handleTransition(() => setCurrentScreen("results"));
      return;
    }

    handleTransition(() => navigateToNextNode(nextNode || null));
  };

  const handleStartFromScratch = async () => {
    trackStartFromScratch();
    setCurrentCourseID(PROD_TIKTOK_COURSE_ID);
    try {
      const { chapterId, lessonId } = await getFirstChapterAndLesson(
        PROD_TIKTOK_COURSE_ID
      );
      navigate(`/chapter/${chapterId}/lesson/${lessonId}/video`);
    } catch (error) {
      console.error("Failed to navigate to first lesson:", error);
      navigate("/");
    }
  };

  const handleContinueToStreak = async () => {
    if (finalLevel) {
      try {
        const { courseId } = await getCourseAndLessonForLevel(finalLevel);
        setCurrentCourseID(courseId);
        handleTransition(() => setCurrentScreen("streak"));
      } catch (error) {
        console.error("Failed to navigate to course:", error);
        handleTransition(() => setCurrentScreen("streak"));
      }
    } else {
      handleTransition(() => setCurrentScreen("streak"));
    }
  };

  const handleStreakContinue = async () => {
    if (finalLevel) {
      try {
        const { chapterId, lessonId } = await getCourseAndLessonForLevel(
          finalLevel
        );
        navigate(`/chapter/${chapterId}/lesson/${lessonId}/video`);
      } catch (error) {
        console.error("Failed to navigate to course:", error);
        navigate("/");
      }
    } else {
      navigate("/");
    }
  };

  // Progress calculation
  const calculateProgress = () => {
    if (currentScreen === "welcome")
      return { step: 0, totalSteps: TOTAL_STEPS };
    if (currentScreen === "instructions")
      return { step: 1, totalSteps: TOTAL_STEPS };
    if (currentScreen === "streak")
      return { step: TOTAL_STEPS, totalSteps: TOTAL_STEPS };
    if (currentScreen === "results")
      return { step: 8, totalSteps: TOTAL_STEPS };

    const baseProgress = nodeStack.length * 2;
    const currentStep = Math.max(
      baseProgress + (currentScreen === "question" ? 3 : 2),
      nodeStack.length * 2
    );

    return { step: currentStep, totalSteps: TOTAL_STEPS };
  };

  // Render helpers
  const renderHeader = () => (
    <div className="w-full appPadding py-0">
      <OnboardingHeader
        step={calculateProgress().step}
        totalSteps={calculateProgress().totalSteps}
        handleBack={() => handleTransition(navigateToPrevNode, "backward")}
        hideBack={currentScreen === "welcome"}
      />
    </div>
  );

  const renderContent = () => {
    switch (currentScreen) {
      case "welcome":
        return (
          <WelcomeScreen
            onStartFromScratch={handleStartFromScratch}
            onFindMyLevel={() => {
              trackBeginPlacementTest();
              handleTransition(() => setCurrentScreen("instructions"));
            }}
            name={firstName}
          />
        );
      case "instructions":
        return (
          <InstructionsScreen
            onContinue={() =>
              handleTransition(() => {
                setCurrentScreen("video");
                setCurrentNode(graph.startNode ?? null);
              })
            }
            text={
              <>
                Lets <span className="font-semibold">find your level</span>!
                Watch the video, then tell me, did you understand about{" "}
                <span className="font-semibold">80% of the words</span>?
              </>
            }
          />
        );
      case "streak":
        return <StreakPage streak={1} onContinue={handleStreakContinue} />;
      case "results":
        return (
          <InstructionsScreen
            onContinue={handleContinueToStreak}
            text={
              <>
                <span className="font-semibold">{finalLevel}</span> looks like
                the right level. Let's start there! You can always adjust this
                later from the Courses tab.
              </>
            }
          />
        );
      case "video":
        if (!currentNode) {
          return null;
        }
        if (lessonDataQuery.isLoading)
          return (
            <div className="w-full h-full flex items-center justify-center">
              <LoadingPage />
            </div>
          );
        if (lessonDataQuery.isError)
          return (
            <PlacementTestPageError
              onRetry={() => window.location.reload()}
              onGoHome={() => navigate("/home/learn")}
            />
          );
        if (!lessonDataQuery.data?.lesson || !lessonDataQuery.data?.video) {
          return (
            <PlacementTestPageError
              onRetry={() => window.location.reload()}
              onGoHome={() => navigate("/home/learn")}
            />
          );
        }
        return (
          <div className="bg-black h-full w-full flex items-center justify-center overflow-clip">
            <PlacementVideo
              videoActivity={lessonDataQuery.data!.video}
              isHorizontal={false}
              onContinue={() =>
                handleTransition(() => setCurrentScreen("question"))
              }
            />
          </div>
        );
      case "question":
        return (
          <QuestionScreen
            onDifficultySelect={(num) => setSelectedAnswer(numberToAnswer[num])}
            onContinue={() => {
              if (selectedAnswer) {
                handleUserAnswer(selectedAnswer);
              }
            }}
            name={firstName}
          />
        );
    }
  };

  return (
    <div className="flex flex-col h-full w-full justify-between items-center overflow-hidden">
      {renderHeader()}
      <div className="flex-grow w-full h-full flex items-center justify-center">
        {renderContent()}
      </div>
    </div>
  );
};

const PlacementTestPageError = ({
  onRetry,
  onGoHome,
}: {
  onRetry?: () => void;
  onGoHome?: () => void;
}) => {
  return (
    <div className="bg-black w-full h-full flex items-center justify-center p-8">
      <div className="text-center max-w-md">
        {/* Error Icon */}
        <div className="flex items-center justify-center mb-4">
          <FaExclamationTriangle className="text-red-500 text-4xl" />
        </div>

        {/* Error Message */}
        <h2 className="text-xl font-semibold text-white mb-3">
          Oops! Something went wrong
        </h2>
        <p className="text-gray-300 mb-8">
          We couldn't load this lesson. This might be a connection issue or a
          temporary server error.
        </p>

        {/* Action Buttons */}
        <div className="flex flex-col gap-3 justify-center max-w-xs mx-auto">
          {onRetry && <ContinueButton onClick={onRetry} text="Try Again" />}
          {onGoHome && (
            <ContinueButton
              backgroundColor="white"
              textColor="black"
              onClick={onGoHome}
              text="Go to Home"
            />
          )}
        </div>

        {/* Help Text */}
        <p className="text-sm text-gray-400 mt-6">
          If this problem persists, please contact our support team at{" "}
          <a
            href="mailto:support@fluentwithparrot.com"
            className="text-blue-400 hover:text-blue-300 underline"
          >
            support@fluentwithparrot.com
          </a>
        </p>
      </div>
    </div>
  );
};

export default PlacementTestPage;
